import { Component, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { PageContainerService } from '../../services';
import { ProjectAskDataComponent } from '../project-ask-data/project-ask-data.component';

/**
 * Application-wide floating button that opens "Ask the data".
 *
 * Mounted once in the app shell rather than per page, so the entry point is available everywhere the
 * user is signed in — the Execution panel's icon is the same dialog reached contextually.
 *
 * Project context comes from the URL. Every project-scoped route in this app carries the ProPak project
 * id as its single ':id' segment (project/:id, readiness-import/:id, and the readiness admin routes), so
 * the id is read from the active URL on each navigation rather than threaded through inputs. When the
 * user is somewhere without a project — the landing page, admin screens — the dialog opens in picker
 * mode and asks which project to ask about, which is why the button stays visible there instead of
 * vanishing and leaving the feature feeling page-specific.
 */
@Component({
  standalone: false,
  selector: 'ask-data-fab',
  templateUrl: './ask-data-fab.component.html',
  styleUrls: ['./ask-data-fab.component.scss'],
})
export class AskDataFabComponent implements OnDestroy {
  /** ProPak project id for the page currently open, or 0 when this page has no project. */
  projectId = 0;

  /** Routes whose single ':id' segment is a ProPak project id. */
  private static readonly ProjectRoutes = [
    'project',
    'edit-project',
    'readiness-import',
    'readiness-admin-form',
    'readiness-form-column-settings',
  ];

  private readonly routerSubscription: Subscription;

  constructor(
    public pcs: PageContainerService,
    private router: Router,
    private dialog: MatDialog) {
    this.projectId = this.readProjectIdFromUrl(this.router.url);
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.projectId = this.readProjectIdFromUrl(event.urlAfterRedirects || event.url);
      });
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  get visible(): boolean {
    return !!(this.pcs && this.pcs.clientSessionService && this.pcs.clientSessionService.loggedIn);
  }

  open() {
    this.dialog.open(ProjectAskDataComponent, {
      width: '860px',
      maxWidth: '96vw',
      height: '80vh',
      maxHeight: '90vh',
      data: { projectId: this.projectId, projectName: '' },
      panelClass: 'custom-dialog-container'
    });
  }

  /**
   * Pull the project id out of a URL like /project/1234?tab=execution. Returns 0 when the route isn't
   * project-scoped, which puts the dialog into picker mode rather than guessing.
   */
  private readProjectIdFromUrl(url: string): number {
    if (!url) {
      return 0;
    }

    // Drop query string and fragment before splitting, so '1234?tab=x' doesn't parse as NaN.
    const path = url.split('?')[0].split('#')[0];
    const segments = path.split('/').filter(segment => !!segment);
    if (segments.length < 2) {
      return 0;
    }

    if (AskDataFabComponent.ProjectRoutes.indexOf(segments[0]) < 0) {
      return 0;
    }

    const parsed = parseInt(segments[1], 10);
    return isNaN(parsed) || parsed <= 0 ? 0 : parsed;
  }
}
